import React from 'react';
import { Link } from 'react-router-dom';
import { ComingSoonPage } from '@components/ui/pages';
import { useAuditEnabled } from '@features/jotaudit';
import { Button, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useTracking } from '../../../context/TrackingContext';
import { EventType } from '../../../types/events';

/**
 * CoachPage Component
 *
 * A page introducing JotCoach, an AI mentor for behavioral health clinics.
 * Provides information about the feature and CTAs for learning more,
 * sending documents, and accessing rules.
 */
export const CoachPage: React.FC = () => {
  const { coach } = useEventTracking();
  const { trackEvent } = useTracking();
  const isAuditEnabled = useAuditEnabled();

  const handleLearnMoreClick = () => {
    coach.learnMoreClicked({
      destination_url: 'https://calendly.com/npeereboom/jotcoach-introduction',
    });
    trackEvent(EventType.EXPRESSED_INTEREST, {
      feature: 'jotcoach',
    });
  };

  const handleSendDocumentsClick = () => {
    coach.sendDocumentsClicked();
    trackEvent(EventType.EXPRESSED_INTEREST, {
      feature: 'jotcoach',
    });
  };

  const handleRulesClick = () => {
    coach.rulesClicked({
      destination_path: '/rules',
    });
  };

  return (
    <ComingSoonPage
      title="JotCoach"
      showHeader={false}
      action={
        isAuditEnabled ? (
          <Button
            component={Link}
            to="/rules"
            variant="contained"
            color="secondary"
            onClick={handleRulesClick}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Your Rules
          </Button>
        ) : undefined
      }
      heroTitle="JotCoach"
      description={
        <>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 3 }}>
            Coach your clinicians to better care, and better documentation.
          </Typography>
          <Typography component="p" sx={{ mb: 2 }}>
            <strong>JotCoach is an AI mentor built for behavioral-health clinics.</strong> It
            improves how your clinicians practice and document — increasing billing accuracy and
            reducing audit risk.
          </Typography>
          <Typography component="p" sx={{ mb: 2 }}>
            Throughout the day, JotCoach delivers{' '}
            <strong>continuous, precision, HIPAA-compliant feedback</strong> directly to each
            clinician, grounded in their real documentation patterns, visits, and notes. It’s{' '}
            <strong>custom-trained on your clinic’s SOPs and documentation rules</strong>, and it
            evolves automatically as your policies change.
          </Typography>
          <Typography component="p">
            Your first 10 coach moments are free, then <strong>just $19 per seat per month.</strong>
          </Typography>
        </>
      }
      primaryAction={{
        label: 'Learn more',
        href: 'https://calendly.com/npeereboom/jotcoach-introduction',
        external: true,
        onClick: handleLearnMoreClick,
        startIcon: null,
      }}
      secondaryAction={{
        label: 'Send us your documents',
        href: 'mailto:docs@jotpsych.com',
        onClick: handleSendDocumentsClick,
        variant: 'outlined',
      }}
    />
  );
};
